import { useState } from 'react';
import { Flex } from '@repo/ui/Flex';
import { Loader } from '@repo/ui/Loader';
import s from './PhotoUpload.module.css';
import { Text, rawString } from '../../Language/Language';
import { Dropzone } from '../../Dropzone/Dropzone';
import {
  uploadImage,
  INVALID_FILE_TYPE_ERROR,
} from '../../../utils/fileUpload/fileUpload';
import type { Photo } from './ReasonsTaskSchema';
import { Button } from '@repo/ui/Button';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export type PhotoUploadProps = {
  max: number;
  photos?: Photo[];
  metadataId?: string;
  onChange: (photos: Photo[]) => void;
  onRemove: (photo: Photo) => void;
  onError: () => void;
};

export const PhotoUpload = (props: PhotoUploadProps) => {
  const { photos = [], max, metadataId, onChange, onError, onRemove } = props;
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<{
    key: string;
    tokens?: Record<string, string>;
  } | null>(null);
  // Track filenames of uploaded photos so we can display them
  const [namesById, setNamesById] = useState<Record<string, string>>({});

  const handleFileUpload = async (files: File[]) => {
    setError(null);
    if (files.length === 0) return;

    if (photos.length + files.length > max) {
      setError({
        key: 'Global.reasons.photos.error.limit',
        tokens: { max: max.toString() },
      });
      return;
    }

    for (const file of files) {
      const validFileSize = file.size <= MAX_FILE_SIZE;
      if (!validFileSize) {
        setError({ key: 'Global.reasons.photos.error.fileSize' });
        return;
      }
    }

    setIsUploading(true);
    try {
      const newPhotos = await Promise.all(
        files.map(async (file) => {
          const id = await uploadImage({ file, metadataId });
          return {
            id,
            metadataId,
            previewUrl: URL.createObjectURL(file),
          };
        }),
      );
      onChange([...photos, ...newPhotos]);
      // Record filenames locally for display
      setNamesById((prev) => {
        const next = { ...prev };
        newPhotos.forEach((photo, idx) => {
          const name = files[idx]?.name;
          if (photo?.id && name) next[photo.id] = name;
        });
        return next;
      });
    } catch (error) {
      if (error instanceof Error && error.message === INVALID_FILE_TYPE_ERROR) {
        setError({ key: 'Global.reasons.photos.error.fileType' });
      } else {
        setError({ key: 'Global.reasons.photos.error.upload' });
      }
      onError();
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <Flex direction="column" gap="g-8">
        {photos.length < max && (
          <Dropzone
            className={s.upload}
            multiple
            accept="image/*"
            onFileDrop={handleFileUpload}
          >
            <Flex padding={['py-24', 'px-16']} direction="column" gap="gy-4">
              {isUploading ? (
                <Loader />
              ) : (
                <>
                  <p className={s.uploadTitle}>
                    <Text langKey="Global.reasons.photos.label" />
                  </p>
                  <p className={s.uploadDescription}>
                    <Text langKey="Global.reasons.photos.description" />
                  </p>
                </>
              )}
            </Flex>
          </Dropzone>
        )}
        {photos?.length > 0 && (
          <>
            {photos.map((photo) => (
              <div key={photo.id} className={s.uploadedCard}>
                <Flex direction="row" alignItems="center" gap="g-12">
                  <div className={s.thumbBox}>
                    {photo.previewUrl && (
                      <img
                        onError={onError}
                        alt={
                          namesById[photo.id] ??
                          rawString({
                            langKey: 'Global.reasons.photos.uploaded',
                          })
                        }
                        src={photo.previewUrl}
                        className={s.thumbImage}
                      />
                    )}
                  </div>
                  <div className={s.uploadedContent}>
                    <div className={s.uploadedTitle}>
                      <Text langKey="Global.reasons.photos.uploaded" />
                    </div>
                    {namesById[photo.id] && (
                      <div
                        className={s.uploadedFilename}
                        title={namesById[photo.id]}
                      >
                        {namesById[photo.id]}
                      </div>
                    )}
                  </div>
                  <Button
                    className={s.removeLink}
                    onClick={() => {
                      onRemove(photo);
                      setNamesById((prev) => {
                        const next = { ...prev };
                        if (photo.id in next) delete next[photo.id];
                        return next;
                      });
                    }}
                    variant="plain"
                  >
                    <Text langKey="Global.reasons.photos.remove" />
                  </Button>
                </Flex>
              </div>
            ))}
          </>
        )}
      </Flex>
      {error && (
        <div className={s.error} role="alert">
          <Text langKey={error.key} replacementTokens={error.tokens} />
        </div>
      )}
    </div>
  );
};
